import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Clock, Droplets, Thermometer, Coffee, Play, Pause, RotateCcw } from 'lucide-react';
import './index.css';

const CoffeeBrewingWidget = () => {
  const [selectedMethod, setSelectedMethod] = useState('chemex');
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  
  // Referencia al contenedor principal para medir altura
  const containerRef = useRef(null);

  const brewingMethods = {
    chemex: {
      name: 'Chemex',
      icon: '🧪',
      color: 'bg-amber-500',
      ratio: '1:15',
      grind: 'Medio-grueso',
      water: '93°C',
      time: '4-5 min',
      steps: [
        { title: 'Preparación', description: 'Coloca el filtro en el Chemex con la parte triple hacia el pico. Enjuaga con agua caliente.', duration: 30 },
        { title: 'Molienda', description: 'Muele 30g de café en grosor medio-grueso. Coloca en el filtro.', duration: 45 },
        { title: 'Blooming', description: 'Vierte 60ml de agua en movimientos circulares. Espera 30-45 segundos.', duration: 45 },
        { title: 'Primera vertida', description: 'Vierte agua hasta 200ml en movimientos lentos y circulares.', duration: 60 },
        { title: 'Segunda vertida', description: 'Continúa vertiendo hasta completar 450ml total.', duration: 90 },
        { title: 'Finalización', description: 'Espera a que termine de gotear. Retira el filtro y sirve.', duration: 60 }
      ]
    },
    aeropress: {
      name: 'AeroPress',
      icon: '💨',
      color: 'bg-blue-500',
      ratio: '1:12',
      grind: 'Fino-medio',
      water: '85°C',
      time: '2-3 min',
      steps: [
        { title: 'Preparación', description: 'Coloca el filtro en la tapa y enjuaga. Ensambla el AeroPress en posición invertida.', duration: 30 },
        { title: 'Café y agua', description: 'Añade 18g de café molido fino-medio. Vierte 200ml de agua caliente.', duration: 30 },
        { title: 'Mezcla', description: 'Revuelve suavemente durante 10 segundos.', duration: 10 },
        { title: 'Reposo', description: 'Deja reposar por 1 minuto y 30 segundos.', duration: 90 },
        { title: 'Volteo', description: 'Coloca la taza, voltea rápidamente y presiona suavemente por 30 segundos.', duration: 30 },
        { title: 'Servir', description: 'Tu café está listo. Puedes diluir con agua caliente si lo prefieres más suave.', duration: 10 }
      ]
    },
    v60: {
      name: 'V60',
      icon: '🌪️',
      color: 'bg-red-500',
      ratio: '1:16',
      grind: 'Medio',
      water: '92°C',
      time: '3-4 min',
      steps: [
        { title: 'Preparación', description: 'Coloca el filtro V60 y enjuaga con agua caliente. Precalienta la taza.', duration: 30 },
        { title: 'Café molido', description: 'Añade 22g de café molido medio. Haz un pequeño hoyo en el centro.', duration: 20 },
        { title: 'Blooming', description: 'Vierte 50ml de agua en espiral desde el centro. Espera 30 segundos.', duration: 30 },
        { title: 'Primera vertida', description: 'Vierte agua hasta 150ml en movimientos circulares lentos.', duration: 45 },
        { title: 'Segunda vertida', description: 'Continúa vertiendo hasta 250ml manteniendo el ritmo.', duration: 45 },
        { title: 'Vertida final', description: 'Completa hasta 350ml. El tiempo total debe ser 3-4 minutos.', duration: 60 }
      ]
    },
    kalita: {
      name: 'Kalita Wave',
      icon: '🌊',
      color: 'bg-teal-500',
      ratio: '1:15',
      grind: 'Medio',
      water: '92°C',
      time: '4-5 min',
      steps: [
        { title: 'Preparación', description: 'Coloca el filtro Kalita Wave y enjuaga. El fondo plano es clave.', duration: 30 },
        { title: 'Café', description: 'Añade 25g de café molido medio. Nivela la superficie.', duration: 20 },
        { title: 'Blooming', description: 'Vierte 50ml de agua desde el centro hacia afuera. Espera 45 segundos.', duration: 45 },
        { title: 'Vertidas graduales', description: 'Vierte en 3-4 etapas hasta completar 375ml, manteniendo nivel constante.', duration: 180 },
        { title: 'Goteo final', description: 'Espera a que termine de gotear completamente.', duration: 60 },
        { title: 'Servir', description: 'Retira el filtro y disfruta tu café con cuerpo equilibrado.', duration: 10 }
      ]
    },
    french: {
      name: 'Prensa Francesa',
      icon: '🫖',
      color: 'bg-purple-500',
      ratio: '1:12',
      grind: 'Grueso',
      water: '93°C',
      time: '4 min',
      steps: [
        { title: 'Precalentamiento', description: 'Enjuaga la prensa francesa con agua caliente para precalentarla.', duration: 30 },
        { title: 'Café molido', description: 'Añade 30g de café molido grueso en el fondo de la prensa.', duration: 20 },
        { title: 'Agua caliente', description: 'Vierte 360ml de agua caliente sobre el café. No llenes completamente.', duration: 30 },
        { title: 'Mezcla inicial', description: 'Revuelve suavemente con una cuchara de madera o plástico.', duration: 15 },
        { title: 'Reposo', description: 'Coloca la tapa sin presionar el émbolo. Espera 4 minutos.', duration: 240 },
        { title: 'Presión y servir', description: 'Presiona el émbolo lentamente y sirve inmediatamente.', duration: 30 }
      ]
    },
    origami: {
      name: 'Origami',
      icon: '🎋',
      color: 'bg-green-500',
      ratio: '1:16',
      grind: 'Medio',
      water: '91°C',
      time: '3-4 min',
      steps: [
        { title: 'Preparación', description: 'Usa filtros cónicos o planos según prefieras. Enjuaga el filtro.', duration: 30 },
        { title: 'Café molido', description: 'Añade 20g de café molido medio. La forma del dripper permite flujo uniforme.', duration: 20 },
        { title: 'Blooming', description: 'Vierte 40ml de agua en espiral. El blooming será más pronunciado.', duration: 30 },
        { title: 'Vertidas controladas', description: 'Vierte en 2-3 etapas hasta 320ml, controlando la velocidad de flujo.', duration: 120 },
        { title: 'Goteo final', description: 'Permite que termine de gotear completamente.', duration: 45 },
        { title: 'Degustación', description: 'El Origami ofrece claridad y permite resaltar las notas del café.', duration: 10 }
      ]
    },
    moka: {
      name: 'Moka',
      icon: '⚡',
      color: 'bg-orange-500',
      ratio: '1:10',
      grind: 'Fino',
      water: 'Fría',
      time: '5-6 min',
      steps: [
        { title: 'Preparación', description: 'Desenrosca la moka. Llena la base con agua fría hasta la válvula.', duration: 30 },
        { title: 'Café molido', description: 'Llena el filtro con café molido fino sin comprimir. Nivela con el dedo.', duration: 30 },
        { title: 'Ensamblaje', description: 'Enrosca bien las partes. Asegúrate de que esté bien sellada.', duration: 20 },
        { title: 'Cocción', description: 'Coloca en fuego medio-bajo. Escucha el borboteo característico.', duration: 300 },
        { title: 'Finalización', description: 'Cuando el sonido cambie a silbido, retira del fuego inmediatamente.', duration: 30 },
        { title: 'Servir', description: 'Mezcla el café y sirve. El resultado es intenso y aromático.', duration: 20 }
      ]
    },
    cold: {
      name: 'Cold Brew',
      icon: '🧊',
      color: 'bg-cyan-500',
      ratio: '1:8',
      grind: 'Grueso',
      water: 'Fría',
      time: '12-24 hrs',
      steps: [
        { title: 'Molienda', description: 'Muele 100g de café en grosor muy grueso, como para prensa francesa.', duration: 60 },
        { title: 'Mezcla', description: 'Combina el café con 800ml de agua fría en un recipiente grande.', duration: 120 },
        { title: 'Mezcla inicial', description: 'Revuelve bien para asegurar que todo el café esté saturado.', duration: 60 },
        { title: 'Extracción', description: 'Deja reposar a temperatura ambiente o en refrigerador por 12-24 horas.', duration: 43200 },
        { title: 'Filtrado', description: 'Filtra el concentrado usando filtro de papel o colador fino.', duration: 300 },
        { title: 'Servir', description: 'Diluye con agua o leche al gusto. Sirve con hielo.', duration: 60 }
      ]
    },
    espresso: {
      name: 'Espresso',
      icon: '☕',
      color: 'bg-yellow-600',
      ratio: '1:2',
      grind: 'Muy fino',
      water: '93°C',
      time: '25-30 seg',
      steps: [
        { title: 'Preparación', description: 'Precalienta la máquina y el portafiltro. Limpia el grupo de extracción.', duration: 60 },
        { title: 'Dosificación', description: 'Pesa 18-20g de café molido muy fino en el portafiltro.', duration: 30 },
        { title: 'Distribución', description: 'Distribuye uniformemente el café y nivela la superficie.', duration: 20 },
        { title: 'Tamping', description: 'Compacta con el tamper aplicando 15kg de presión de forma pareja.', duration: 15 },
        { title: 'Extracción', description: 'Inicia la extracción. Debe fluir como "cola de ratón" dorada.', duration: 27 },
        { title: 'Resultado', description: 'Obtendrás 36-40ml de espresso con crema dorada. Sirve inmediatamente.', duration: 10 }
      ]
    }
  };

  const currentMethod = brewingMethods[selectedMethod];

  const startTimer = () => {
    if (!isPlaying) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);
      setIsPlaying(true);
    } else {
      clearInterval(timerInterval);
      setIsPlaying(false);
    }
  };

  const resetTimer = () => {
    clearInterval(timerInterval);
    setTimer(0);
    setIsPlaying(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const nextStep = () => {
    if (currentStep < currentMethod.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Función para enviar la altura al parent
  const sendHeightToParent = () => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight;
      // Enviar mensaje al parent con la altura
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ 
          type: 'resize-iframe', 
          height: height,
          appId: 'coffee-brewing-widget'
        }, '*');
      }
    }
  };

  // Efecto para enviar la altura al parent después de cada renderizado
  useEffect(() => {
    // Pequeño timeout para asegurar que el DOM se ha actualizado completamente
    const timeoutId = setTimeout(() => {
      sendHeightToParent();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedMethod, currentStep, isPlaying, timer]); // Dependencias que pueden afectar la altura

  // Efecto para manejar cambios de tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      sendHeightToParent();
    };

    window.addEventListener('resize', handleResize);
    
    // Enviar altura inicial
    sendHeightToParent();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Limpieza del intervalo cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  return (
    <div ref={containerRef} className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">☕ Guía Interactiva de Preparación de Café</h1>
        <p className="text-gray-600">Domina todos los métodos de extracción paso a paso</p>
      </div>

      {/* Method Selector */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 mb-8">
        {Object.entries(brewingMethods).map(([key, method]) => (
          <button
            key={key}
            onClick={() => {
              setSelectedMethod(key);
              setCurrentStep(0);
              resetTimer();
            }}
            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
              selectedMethod === key
                ? `${method.color} text-white border-transparent`
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-2xl mb-1">{method.icon}</div>
            <div className="text-xs font-medium">{method.name}</div>
          </button>
        ))}
      </div>

      {/* Method Info Card */}
      <div className={`${currentMethod.color} text-white p-6 rounded-lg mb-6`}>
        <div className="flex items-center mb-4">
          <span className="text-4xl mr-4">{currentMethod.icon}</span>
          <div>
            <h2 className="text-2xl font-bold">{currentMethod.name}</h2>
            <p className="text-white/90">Método de extracción por {currentMethod.name.toLowerCase()}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center">
            <Coffee className="w-5 h-5 mr-2" />
            <div>
              <div className="text-sm opacity-90">Proporción</div>
              <div className="font-semibold">{currentMethod.ratio}</div>
            </div>
          </div>
          <div className="flex items-center">
            <Droplets className="w-5 h-5 mr-2" />
            <div>
              <div className="text-sm opacity-90">Molienda</div>
              <div className="font-semibold">{currentMethod.grind}</div>
            </div>
          </div>
          <div className="flex items-center">
            <Thermometer className="w-5 h-5 mr-2" />
            <div>
              <div className="text-sm opacity-90">Temperatura</div>
              <div className="font-semibold">{currentMethod.water}</div>
            </div>
          </div>
          <div className="flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            <div>
              <div className="text-sm opacity-90">Tiempo</div>
              <div className="font-semibold">{currentMethod.time}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Timer Controls */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={startTimer}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              isPlaying ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
            }`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPlaying ? 'Pausar' : 'Iniciar'}
          </button>
          <div className="text-2xl font-mono font-bold text-gray-800">
            {formatTime(timer)}
          </div>
          <button
            onClick={resetTimer}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg font-medium"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Steps Tutorial */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            Paso {currentStep + 1} de {currentMethod.steps.length}
          </h3>
          <div className="text-sm text-gray-500">
            Duración sugerida: {Math.floor(currentMethod.steps[currentStep].duration / 60)}:
            {(currentMethod.steps[currentStep].duration % 60).toString().padStart(2, '0')}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className={`h-2 rounded-full ${currentMethod.color} transition-all duration-300`}
            style={{ width: `${((currentStep + 1) / currentMethod.steps.length) * 100}%` }}
          ></div>
        </div>

        {/* Current Step */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            {currentMethod.steps[currentStep].title}
          </h4>
          <p className="text-gray-600 leading-relaxed">
            {currentMethod.steps[currentStep].description}
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>
          
          <button
            onClick={nextStep}
            disabled={currentStep === currentMethod.steps.length - 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              currentStep === currentMethod.steps.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
            }`}
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoffeeBrewingWidget;
